import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import { PREDEFINED_PROJECT_ADMIN_SETTINGS_TABS_IDS } from '@adsk/acs-admin-ui-components-helpers';
import SettingsTabs from '@adsk/acs-admin-ui-components-settings-tabs';

const PROJECT_ADMIN_PRODUCT_PREFIX = 'project-admin';

const ProjectAdminSettingsTabs = ({
  projectId,
  showLocations,
  showNotifications,
  mainTab,
  additionalTabs,
  children,
  onProfileSave,
  onTabChange,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

  const memoizedTabs = useMemo(() => {
    let tabs = [];

    const getSettingsTabDefinition = (tabName) => {
      const tab = {
        tab: tabName,
        label: T.translate(tabName.toUpperCase()),
      };

      if (mainTab === tabName) {
        tab.content = children;
      } else {
        tab.url = `/${PROJECT_ADMIN_PRODUCT_PREFIX}/projects/${projectId}/${tabName}`;
      }

      return tab;
    };

    if (showLocations) {
      tabs.push(
        getSettingsTabDefinition(
          PREDEFINED_PROJECT_ADMIN_SETTINGS_TABS_IDS.LOCATIONS
        )
      );
    }

    if (showNotifications) {
      tabs.push(
        getSettingsTabDefinition(
          PREDEFINED_PROJECT_ADMIN_SETTINGS_TABS_IDS.NOTIFICATIONS
        )
      );
    }

    if (additionalTabs) tabs = tabs.concat(additionalTabs);

    return tabs;
  }, [
    T,
    children,
    mainTab,
    additionalTabs,
    projectId,
    showLocations,
    showNotifications,
  ]);

  return (
    <SettingsTabs
      tabs={memoizedTabs}
      activeTab={mainTab}
      onProfileSave={onProfileSave}
      onTabChange={onTabChange}
    />
  );
};

export const tabsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    tab: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    url: PropTypes.string,
    content: PropTypes.node,
    labelProps: PropTypes.object,
  })
);

ProjectAdminSettingsTabs.propTypes = {
  projectId: PropTypes.string.isRequired,
  showLocations: PropTypes.bool.isRequired,
  showNotifications: PropTypes.bool.isRequired,
  mainTab: PropTypes.string,
  additionalTabs: tabsPropType,
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  onProfileSave: PropTypes.func,
  onTabChange: PropTypes.func,
};

export default ProjectAdminSettingsTabs;
